import { Link, Spacer, Typography, useSnackbar } from "@fullscript/aviary-web";
import { Trans, useTranslation } from "react-i18next";

import { l } from "@shared/locales/i18n";
import {
  useResendSignInMfaPasscodeSharedMutation as useResendMFAPasscode,
  type ResendSignInMfaPasscodeSharedMutation as ResendMFAPasscodeData,
} from "@shared/MultifactorAuthentication/data/ResendSignInMFAPasscode.mutation.generated";
import { TwoFactorAuthenticationMethods } from "@shared/types/graphqlGenerated";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";

import * as styles from "./ResendOtp.styles";

interface Props {
  token: string;
  otpMethod: TwoFactorAuthenticationMethods;
  email?: string;
  phoneLast4?: string;
}

const ResendOtp = ({ token, otpMethod, email, phoneLast4 }: Props) => {
  const { t } = useTranslation();
  const { criticalSnackbar, successSnackbar } = useSnackbar();

  const onResendPasscodeComplete = (data: ResendMFAPasscodeData) => {
    const hasErrors = data?.auth?.userResendTwoFactorAuthentication?.errors?.message;
    const isSent = data?.auth?.userResendTwoFactorAuthentication?.sent;
    const isEmailOtpMethod = otpMethod === TwoFactorAuthenticationMethods.Email;

    if (hasErrors || !isSent) {
      const errorMessage = isEmailOtpMethod ? t(l.mfa.emailNotSent) : t(l.mfa.textMessageNotSent);

      return criticalSnackbar(errorMessage);
    }

    if (isSent) {
      const successMessage = isEmailOtpMethod
        ? t(l.mfa.emailHasBeenSent, { email })
        : t(l.mfa.textMessageHasBeenSent, { phoneLast4 });

      successSnackbar(successMessage);
    }
  };

  const [resendPasscode, { loading: loadingResendPasscode }] = useResendMFAPasscode({
    onCompleted: onResendPasscodeComplete,
    onError: ({ message }) => criticalSnackbar(message),
  });

  const onResendPasscode = () => {
    if (!loadingResendPasscode) {
      gRecaptchaExecute(captchaToken => {
        resendPasscode({
          variables: {
            input: {
              token,
              captchaToken,
            },
          },
        });
      });
    }
  };

  const renderResendText = () => {
    if (otpMethod === TwoFactorAuthenticationMethods.Sms) {
      return (
        <Typography variant="footnote" isMarginless intention="neutral" colorIntensity="subtle">
          <Trans i18nKey={l.mfa.didntGetTextMessage}>
            Didn't get the text message?
            <Link onClick={onResendPasscode}>Resend</Link>.
          </Trans>
        </Typography>
      );
    }

    return (
      <Typography variant="footnote" isMarginless intention="neutral" colorIntensity="subtle">
        <Trans i18nKey={l.mfa.didntGetEmail}>
          Didn't get the email? Check your spam folder or
          <Link onClick={onResendPasscode}>resend</Link>.
        </Trans>
      </Typography>
    );
  };

  return (
    <div css={styles.wrapper}>
      {renderResendText()}
      <Spacer size="small" />
      <Typography
        variant="footnote"
        customCss={styles.expirationMessage}
        isMarginless
        intention="neutral"
        colorIntensity="subtle"
      >
        {t(l.mfa.VerifyMFAForm.codeWillExpire)}
      </Typography>
    </div>
  );
};

export { ResendOtp };
